import type { DateRange, PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import type { IntlShape } from "react-intl";

export type ShortcutItemType = "today" | "last24h" | "yesterday" | "last7Days" | "last14Days" | "last30Days";
export interface ShortcutItem extends PickersShortcutsItem<DateRange<DateTime>> {
	type: ShortcutItemType;
}

export const getShortcutItems = (
	timezone: string | undefined,
	intl: IntlShape,
	// default to 5 minutes as that is the default for the time picker
	minuteStep: number = 5,
): ShortcutItem[] => {
	const timezoneToUse = timezone || "local";
	const today = DateTime.now().setZone(timezoneToUse);

	return [
		{
			id: "today",
			type: "today",
			label: intl.formatMessage({ id: "date-time.today", defaultMessage: "Today" }),
			getValue: () => {
				return [today.startOf("day"), today.endOf("day")];
			},
		},
		{
			id: "last24h",
			type: "last24h",
			label: intl.formatMessage({ id: "date-time.last-24-hours", defaultMessage: "Last 24 hours" }),
			getValue: () => {
				return [
					today
						.minus({ hours: 24 })
						.set({ minute: Math.ceil(today.minute / minuteStep) * minuteStep, second: 0, millisecond: 0 }),
					today.set({ minute: Math.ceil(today.minute / minuteStep) * minuteStep, second: 0, millisecond: 0 }),
				];
			},
		},
		{
			id: "yesterday",
			type: "yesterday",
			label: intl.formatMessage({ id: "date-time.yesterday", defaultMessage: "Yesterday" }),
			getValue: () => {
				const yesterday = today.minus({ days: 1 });

				return [yesterday.startOf("day"), yesterday.endOf("day")];
			},
		},
		{
			id: "last7Days",
			type: "last7Days",
			label: intl.formatMessage({ id: "date-time.last-7-days", defaultMessage: "Last 7 days" }),
			getValue: () => {
				return [today.minus({ days: 7 }).startOf("day"), today.endOf("day")];
			},
		},
		{
			id: "last14Days",
			type: "last14Days",
			label: intl.formatMessage({ id: "date-time.last-14-days", defaultMessage: "Last 14 days" }),
			getValue: () => {
				return [today.minus({ days: 14 }).startOf("day"), today.endOf("day")];
			},
		},
		{
			id: "last30Days",
			type: "last30Days",
			label: intl.formatMessage({ id: "date-time.last-30-days", defaultMessage: "Last 30 days" }),
			getValue: () => {
				return [today.minus({ days: 30 }).startOf("day"), today.endOf("day")];
			},
		},
	];
};
